import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import './CarCarousel.css';

const TOTAL_SLIDES = 6;
const AUTO_PLAY_INTERVAL = 4000;

const CarCarousel = () => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);
  const touchStartX = useRef(0);

  const goTo = useCallback((index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 500);
  }, [isTransitioning]);

  const next = useCallback(() => {
    goTo((current + 1) % TOTAL_SLIDES);
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + TOTAL_SLIDES) % TOTAL_SLIDES);
  }, [current, goTo]);

  useEffect(() => {
    timerRef.current = setInterval(next, AUTO_PLAY_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [next]);

  const resetTimer = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(next, AUTO_PLAY_INTERVAL);
  }, [next]);

  const handlePrev = () => { prev(); resetTimer(); };
  const handleNext = () => { next(); resetTimer(); };
  const handleDot = (i) => { goTo(i); resetTimer(); };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const delta = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 50) {
      if (delta > 0) handleNext();
      else handlePrev();
    }
  };

  const raw = t('carousel.items');
  const items = Array.isArray(raw) ? raw : [];

  return (
    <section className="section carousel-section">
      <div className="container">
        <h2 className="section-title">{t('carousel.title')}</h2>
        <p className="section-subtitle">{t('carousel.subtitle')}</p>

        <div
          className="carousel"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <button
            className="carousel__arrow carousel__arrow--left"
            onClick={handlePrev}
            aria-label="Previous"
          >
            <svg viewBox="0 0 24 24" width="28" height="28">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor"/>
            </svg>
          </button>

          <div className="carousel__viewport">
            <div
              className="carousel__track"
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {Array.from({ length: TOTAL_SLIDES }, (_, i) => (
                <div key={i} className="carousel__slide">
                  <img
                    src={`/images/cars/${i + 1}.png`}
                    alt={items[i] || `${t('carousel.imageAlt')} ${i + 1}`}
                    className="carousel__image"
                    draggable={false}
                  />
                  {items[i] && (
                    <div className="carousel__caption">{items[i]}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button
            className="carousel__arrow carousel__arrow--right"
            onClick={handleNext}
            aria-label="Next"
          >
            <svg viewBox="0 0 24 24" width="28" height="28">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" fill="currentColor"/>
            </svg>
          </button>
        </div>

        <div className="carousel__dots">
          {Array.from({ length: TOTAL_SLIDES }, (_, i) => (
            <button
              key={i}
              className={`carousel__dot ${i === current ? 'carousel__dot--active' : ''}`}
              onClick={() => handleDot(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>

        <div className="carousel__counter">
          {current + 1} / {TOTAL_SLIDES}
        </div>
      </div>
    </section>
  );
};

export default CarCarousel;
